import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Camera, Upload, Loader2, ScanLine, RotateCcw, UserPlus } from "lucide-react";
import { toast } from "sonner";
import CompanySearchSelect from "../components/companies/CompanySearchSelect";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    company_id: "",
    company_name: "",
    city: "",
    notes: "",
};

export default function ScanCard() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canCreateCompany = hasPermission("companies_manage");
    const cameraRef = useRef(null);
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanned, setScanned] = useState(false);
    const [rawText, setRawText] = useState("");
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    useEffect(() => () => {
        if (preview) URL.revokeObjectURL(preview);
    }, [preview]);

    const choose = (f) => {
        if (!f) return;
        if (preview) URL.revokeObjectURL(preview);
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setScanned(false);
        setRawText("");
        setForm(EMPTY);
    };

    const startOver = () => {
        if (preview) URL.revokeObjectURL(preview);
        setFile(null);
        setPreview(null);
        setScanned(false);
        setRawText("");
        setForm(EMPTY);
    };

    const scan = async () => {
        if (!file) return;
        setScanning(true);
        try {
            const fd = new FormData();
            fd.append("file", file, file.name || "card.jpg");
            const { data } = await api.post("/ocr/business-card", fd);
            setRawText(data.raw_text || "");
            setForm({
                first_name: data.first_name || "",
                last_name: data.last_name || "",
                email: data.email || "",
                phone: data.phone || "",
                position: data.position || "",
                company_id: "",
                company_name: data.company_name || "",
                city: "",
                notes: "",
            });
            setScanned(true);
            toast.success("Tarjeta procesada — revisa los datos");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (formatApiError(detail) || "No se pudo procesar la imagen"));
        } finally {
            setScanning(false);
        }
    };

    const save = async (e) => {
        e?.preventDefault?.();
        if (!form.first_name.trim()) {
            toast.error("El nombre es obligatorio");
            return;
        }
        if (!form.company_id) {
            toast.error("Selecciona una empresa existente");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim() || "",
                email: form.email.trim() || null,
                phone: form.phone.trim() || null,
                position: form.position.trim() || null,
                company_id: form.company_id,
                city: form.city.trim() || null,
                notes: form.notes.trim() || null,
                source: "business_card_scan",
            };
            const { data } = await api.post("/leads/share", payload);
            toast.success(`Contacto guardado: ${data.first_name || ""} ${data.last_name || ""}`.trim());
            navigate("/leads", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409) {
                toast.error(
                    typeof detail === "object" && detail?.message
                        ? detail.message
                        : "Ya existe un contacto con esos datos"
                );
            } else {
                toast.error(formatApiError(detail) || "No se pudo guardar el contacto");
            }
        } finally {
            setSaving(false);
        }
    };

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <Layout
            title="Escanear tarjeta"
            subtitle="Captura una tarjeta de presentación y guárdala como contacto"
            data-testid="scan-card-page"
        >
            <div className="max-w-2xl mx-auto space-y-4">
                {!scanned && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                                <ScanLine className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="font-display font-semibold text-slate-900">Captura la tarjeta</h2>
                                <p className="text-sm text-slate-500">Usa la cámara o sube una imagen. Procesamos el texto con Google Vision para prellenar el formulario.</p>
                            </div>
                        </div>

                        {preview ? (
                            <div className="space-y-3">
                                <img
                                    src={preview}
                                    alt="Tarjeta capturada"
                                    className="w-full max-h-80 object-contain rounded-md border border-slate-200 bg-slate-50"
                                    data-testid="scan-preview"
                                />
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                    <Button variant="ghost" onClick={startOver} disabled={scanning} data-testid="scan-retake">
                                        <RotateCcw className="w-4 h-4 mr-2" />Retomar
                                    </Button>
                                    <Button onClick={scan} disabled={scanning} data-testid="scan-process">
                                        {scanning ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
                                        ) : (
                                            <><ScanLine className="w-4 h-4 mr-2" />Procesar tarjeta</>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                    ref={cameraRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    className="hidden"
                                    onChange={(e) => choose(e.target.files?.[0])}
                                    data-testid="scan-camera-input"
                                />
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => choose(e.target.files?.[0])}
                                    data-testid="scan-file-input"
                                />
                                <Button
                                    onClick={() => cameraRef.current?.click()}
                                    className="h-24 flex-col gap-1"
                                    data-testid="scan-take-photo"
                                >
                                    <Camera className="w-6 h-6" />
                                    Tomar foto
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => fileRef.current?.click()}
                                    className="h-24 flex-col gap-1"
                                    data-testid="scan-upload"
                                >
                                    <Upload className="w-6 h-6" />
                                    Subir imagen
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {scanned && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-4 gap-3">
                            <h2 className="font-display font-semibold text-slate-900">Revisa y guarda</h2>
                            <Button variant="ghost" size="sm" onClick={startOver} data-testid="scan-restart">
                                <RotateCcw className="w-4 h-4 mr-2" />Escanear otra
                            </Button>
                        </div>
                        {preview && (
                            <img
                                src={preview}
                                alt="Tarjeta"
                                className="w-full max-h-40 object-contain rounded-md border border-slate-200 bg-slate-50 mb-4"
                            />
                        )}
                        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="scan-form">
                            <div>
                                <Label>Nombre *</Label>
                                <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required data-testid="scan-form-first-name" />
                            </div>
                            <div>
                                <Label>Apellido</Label>
                                <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="scan-form-last-name" />
                            </div>
                            <div>
                                <Label>Correo</Label>
                                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="scan-form-email" />
                            </div>
                            <div>
                                <Label>Teléfono</Label>
                                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="scan-form-phone" />
                            </div>
                            <div>
                                <Label>Cargo</Label>
                                <Input value={form.position} onChange={(e) => update("position", e.target.value)} data-testid="scan-form-position" />
                            </div>
                            <div>
                                <Label>Empresa *</Label>
                                <CompanySearchSelect
                                    value={form.company_id}
                                    onChange={(company_id, company) => setForm((current) => ({ ...current, company_id, company_name: company?.name || current.company_name }))}
                                    canCreate={canCreateCompany}
                                    data-testid="scan-form-company"
                                />
                                {form.company_name && !form.company_id && <p className="mt-1 text-xs text-slate-500">Detectado en la tarjeta: {form.company_name}. Busca y selecciona la empresa existente.</p>}
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Ciudad</Label>
                                <Input value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="scan-form-city" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Notas (evento, intereses, etc.)</Label>
                                <Textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="scan-form-notes" />
                            </div>
                            {rawText && (
                                <details className="sm:col-span-2 text-xs text-slate-500">
                                    <summary className="cursor-pointer">Ver texto detectado por OCR</summary>
                                    <pre className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded whitespace-pre-wrap break-words">{rawText}</pre>
                                </details>
                            )}
                            <div className="sm:col-span-2 flex items-center justify-end gap-2 mt-2">
                                <Button type="button" variant="ghost" onClick={() => navigate("/leads")} disabled={saving}>
                                    Cancelar
                                </Button>
                                <Button type="submit" disabled={saving} data-testid="scan-form-save">
                                    {saving ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                                    ) : (
                                        <><UserPlus className="w-4 h-4 mr-2" />Guardar contacto</>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </Layout>
    );
}
